import type { LocationQuery, LocationQueryRaw } from 'vue-router';

const ALLOWED_SOURCES = new Set(['catalog', 'search', 'reco', 'home']);
const LID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;

export interface AttributionMarkers {
  lid: string;
  source: string;
}

const normalizeLid = (value: unknown): string => {
  const lid = String(value ?? '').trim();
  if (!lid || !LID_PATTERN.test(lid)) {
    return '';
  }
  return lid;
};

const normalizeSource = (value: unknown): string => {
  const source = String(value ?? '').trim().toLowerCase();
  if (!source || !ALLOWED_SOURCES.has(source)) {
    return '';
  }
  return source;
};

const firstQueryValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return String(value[0] ?? '');
  }
  return String(value ?? '');
};

export const extractAttributionFromQuery = (query: LocationQuery | URLSearchParams | string): AttributionMarkers => {
  if (typeof query === 'string') {
    const params = new URLSearchParams(query.startsWith('?') ? query : `?${query}`);
    return {
      lid: normalizeLid(params.get('lid')),
      source: normalizeSource(params.get('source')),
    };
  }

  if (query instanceof URLSearchParams) {
    return {
      lid: normalizeLid(query.get('lid')),
      source: normalizeSource(query.get('source')),
    };
  }

  return {
    lid: normalizeLid(firstQueryValue((query as LocationQuery).lid)),
    source: normalizeSource(firstQueryValue((query as LocationQuery).source)),
  };
};

export const hasAttribution = (markers: AttributionMarkers): boolean => Boolean(markers.lid || markers.source);

export const mergeAttributionIntoQuery = (
  query: LocationQuery | LocationQueryRaw,
  markers: AttributionMarkers,
): LocationQueryRaw => {
  const merged: LocationQueryRaw = { ...(query as LocationQueryRaw) };
  if (markers.lid) {
    merged.lid = markers.lid;
  }
  if (markers.source) {
    merged.source = markers.source;
  }
  return merged;
};

export const appendAttributionToUrl = (url: string, markers: AttributionMarkers): string => {
  if (!hasAttribution(markers)) {
    return url;
  }
  const isAbsolute = /^https?:\/\//i.test(url);
  try {
    const parsed = new URL(url, window.location.origin);
    if (markers.lid) {
      parsed.searchParams.set('lid', markers.lid);
    }
    if (markers.source) {
      parsed.searchParams.set('source', markers.source);
    }
    if (isAbsolute) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_err) {
    return url;
  }
};

export const currentUrlAttribution = (): AttributionMarkers => {
  if (typeof window === 'undefined') {
    return { lid: '', source: '' };
  }
  return extractAttributionFromQuery(window.location.search);
};

export const cleanupAttributionInCurrentUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has('lid') && !url.searchParams.has('source')) {
    return;
  }
  url.searchParams.delete('lid');
  url.searchParams.delete('source');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};
